import { useState } from 'react';
import {
  Users, Package, Mail, ShieldCheck, MessageSquare, Zap,
  TrendingUp, Settings, ClipboardList, UserMinus, Bell, HardDrive,
  Menu, X, ChevronRight, Send, LayoutDashboard, Newspaper
} from 'lucide-react';

const SECTIONS = [
  { group: 'Pilotage', items: [
    { key: 'pilotage', label: 'Pilotage', icon: LayoutDashboard },
  ]},
  { group: 'Gestion', items: [
    { key: 'verifications', label: 'Verifications', icon: ShieldCheck },
    { key: 'users', label: 'Utilisateurs', icon: Users },
    { key: 'listings', label: 'Annonces', icon: Package },
    { key: 'boosts', label: 'Boosts', icon: Zap },
  ]},
  { group: 'Suivi', items: [
    { key: 'stats', label: 'Statistiques', icon: TrendingUp },
    { key: 'messages', label: 'Messages', icon: MessageSquare },
    { key: 'notifications', label: 'Notifications', icon: Bell },
    { key: 'contacts', label: 'Contacts', icon: Mail },
    { key: 'deleted', label: 'Supprimes', icon: UserMinus },
    { key: 'logs', label: 'Journal', icon: ClipboardList },
  ]},
  { group: 'Configuration', items: [
    { key: 'blog', label: 'Articles', icon: Newspaper },
    { key: 'sequences', label: 'Sequences', icon: Send },
    { key: 'newsletter', label: 'Newsletter', icon: Mail },
    { key: 'settings', label: 'Parametres', icon: Settings },
    { key: 'backup', label: 'Backup', icon: HardDrive },
  ]},
];

export function AdminNav({ activeTab, setActiveTab, badges, onTabClick }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSelect = (key) => {
    setActiveTab(key);
    if (onTabClick) onTabClick(key);
    setSidebarOpen(false);
  };

  const activeItem = SECTIONS.flatMap(s => s.items).find(i => i.key === activeTab);
  const ActiveIcon = activeItem?.icon || ShieldCheck;

  return (
    <>
      {/* Mobile: Current tab button + sidebar */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="w-full flex items-center justify-between bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl px-4 py-3 text-left"
          data-testid="admin-mobile-nav-btn"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#1F3F72]/10 dark:bg-[#1F3F72]/25 flex items-center justify-center">
              <ActiveIcon className="w-4 h-4 text-[#1F3F72] dark:text-blue-300" />
            </div>
            <div>
              <p className="font-semibold text-sm text-neutral-900 dark:text-white">{activeItem?.label || 'Menu'}</p>
              <p className="text-[10px] text-neutral-400">Changer de section</p>
            </div>
          </div>
          <Menu className="w-5 h-5 text-neutral-400" />
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="admin-mobile-sidebar">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white dark:bg-neutral-900 border-l dark:border-neutral-700 overflow-y-auto animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between p-4 border-b dark:border-neutral-700">
              <h2 className="font-bold text-base text-neutral-900 dark:text-white">Navigation</h2>
              <button onClick={() => setSidebarOpen(false)} className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                <X className="w-4 h-4 text-neutral-500" />
              </button>
            </div>
            <nav className="p-3">
              {SECTIONS.map(group => (
                <div key={group.group} className="mb-4">
                  <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider px-3 mb-2">{group.group}</p>
                  {group.items.map(item => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.key;
                    const badge = badges?.[item.key];
                    return (
                      <button
                        key={item.key}
                        onClick={() => handleSelect(item.key)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-colors text-left ${
                          isActive
                            ? 'bg-[#1F3F72] text-white'
                            : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                        }`}
                        data-testid={`admin-tab-${item.key}`}
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="text-sm font-medium flex-1">{item.label}</span>
                        {badge > 0 && (
                          <span className={`text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0 ${
                            isActive ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                          }`}>{badge}</span>
                        )}
                        <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-white/50' : 'text-neutral-300 dark:text-neutral-600'}`} />
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop: Grouped tabs */}
      <div className="hidden md:flex items-center gap-1 mb-6 flex-wrap bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-1.5 border dark:border-neutral-700" data-testid="admin-desktop-nav">
        {SECTIONS.map((group, gi) => (
          <div key={group.group} className="flex items-center">
            {gi > 0 && <div className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1.5" />}
            {group.items.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.key;
              const badge = badges?.[item.key];
              return (
                <button
                  key={item.key}
                  onClick={() => handleSelect(item.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    isActive
                      ? 'bg-[#1F3F72] text-white shadow-sm'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-white dark:hover:bg-neutral-700 hover:text-neutral-900 dark:hover:text-white'
                  }`}
                  data-testid={`admin-tab-${item.key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{item.label}</span>
                  {badge > 0 && (
                    <span className={`text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center ${
                      isActive ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                    }`}>{badge}</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}
